// src/pages/modules/ModulesPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Stack,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  Business as BusinessIcon,
  VpnKey as VpnKeyIcon,
  Login as LoginIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';

// Hooks
import { useModules } from '../../../hooks/api/useModules';
import { useDepartments } from '../hooks/useDepartment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useDialog } from '../../../hooks/mui/useDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

import PageHeader from '../../../components/common/PageHeader';

export default function ModulesPage() {
  const { t } = useTranslation(["modules", "common"]);
  const { showSuccess, showError } = useSnackbar();
  const permissions = usePermissions();

  // Dialogs
  const moduleDetailsDialog = useDialog();
  const departmentModulesDialog = useDialog();

  // Local state
  const [selectedModule, setSelectedModule] = useState(null);
  const [modulePermissions, setModulePermissions] = useState([]);
  const [departmentModulesList, setDepartmentModulesList] = useState([]);
  const [expandedModule, setExpandedModule] = useState(null);

  // API hooks
  const {
    modules,
    loading: modulesLoading,
    error: modulesError,
    clearError: clearModulesError,
    fetchAllModules,
    getModulePermissions,
  } = useModules();

  const {
    departments,
    loading: departmentsLoading,
    fetchAllDepartments,
    getDepartmentModules,
  } = useDepartments();

  // Load initial data
  useEffect(() => {
    fetchAllModules();
    fetchAllDepartments({ include_deactivated: false });
  }, []);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchAllModules();
    fetchAllDepartments({ include_deactivated: false });
  }, [fetchAllModules, fetchAllDepartments]);

  const handleModuleDetails = useCallback(async (module) => {
    try {
      const result = await getModulePermissions(module.module_id);
      if (result.success) {
        setSelectedModule(module);
        setModulePermissions(result.data.permissions || []);
        moduleDetailsDialog.openDialog(module);
      }
    } catch (error) {
      showError(t('modules:errors.load_permissions_failed'));
    }
  }, [getModulePermissions, moduleDetailsDialog, showError, t]);

  const handleViewDepartments = useCallback(async (module) => {
    try {
      // Get all departments and check which ones have this module enabled
      const departmentModules = await Promise.all(
        departments.map(async (dept) => {
          try {
            const result = await getDepartmentModules(dept.department_id);
            const hasModule = result.success && 
              result.data.modules?.some(m => m.module_id === module.module_id);
            return {
              ...dept,
              hasModule,
              moduleData: hasModule ? 
                result.data.modules?.find(m => m.module_id === module.module_id) : null
            };
          } catch (error) {
            return { ...dept, hasModule: false, moduleData: null };
          }
        })
      );
      
      setDepartmentModulesList(departmentModules);
      departmentModulesDialog.openDialog(module);
    } catch (error) {
      showError(t('modules:errors.load_departments_failed'));
    }
  }, [departments, getDepartmentModules, departmentModulesDialog, showError, t]);

  const handleLoginToModule = useCallback((module, department = null) => {
    // This is now handled by the ModuleLoginButton component
    // keeping this method for compatibility but it's no longer used
  }, []);

  const handleAccordionChange = useCallback((moduleId) => (event, isExpanded) => {
    setExpandedModule(isExpanded ? moduleId : null);
  }, []);

  // Check permissions
  const canRead = permissions.hasScope('module:read');
  const canManage = permissions.hasScope('module:*') || permissions.isAdmin;

  return (
    <Box>
      {/* Header */}
      <PageHeader
        icon={ExtensionIcon}
        title={t('modules:title')}
        subtitle={t('modules:subtitle')}
        actions={<>
          <Tooltip title={t('modules:actions.refresh')}>
            <IconButton onClick={handleRefresh} disabled={modulesLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </>}
      />

      {/* Error Alert */}
      {modulesError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearModulesError}>
          {modulesError}
        </Alert>
      )}

      {/* Modules List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('modules:sections.system_modules')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('modules:sections.system_modules_description')}
          </Typography>

          {modules.length === 0 ? (
            <Box textAlign="center" py={6}>
              <ExtensionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('modules:messages.no_modules')}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {t('modules:messages.no_modules_description')}
              </Typography>
            </Box>
          ) : (
            <Box>
              {modules.map((module) => (
                <Accordion
                  key={module.module_id}
                  expanded={expandedModule === module.module_id}
                  onChange={handleAccordionChange(module.module_id)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} flex={1}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <ExtensionIcon />
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6" fontWeight="bold">
                          {module.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {module.description}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Chip
                          label={`${module.module_id.substring(0, 8)}...`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      {/* Module Actions */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('modules:sections.actions')}
                        </Typography>
                        <Stack spacing={2}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<VpnKeyIcon />}
                            onClick={() => handleModuleDetails(module)}
                          >
                            {t('modules:actions.view_permissions')}
                          </Button>
                          
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<BusinessIcon />}
                            onClick={() => handleViewDepartments(module)}
                          >
                            {t('modules:actions.view_department_assignments')}
                          </Button>
                        </Stack>
                      </Grid>

                      {/* Module Info */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('modules:module_information')}
                        </Typography>
                        <Stack spacing={1}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('modules:labels.module_id')}
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace">
                              {module.module_id}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('modules:labels.name')}
                            </Typography>
                            <Typography variant="body2">
                              {module.name}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('modules:labels.description')}
                            </Typography>
                            <Typography variant="body2">
                              {module.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Module Details Dialog */}
      <Dialog
        open={moduleDetailsDialog.open}
        onClose={moduleDetailsDialog.closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <ExtensionIcon />
            {t('modules:dialogs.permissions_title', { name: selectedModule?.name })}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('modules:dialogs.permissions_description', { name: selectedModule?.name })}
          </Typography>

          {modulePermissions.length === 0 ? (
            <Alert severity="info">
              {t('modules:messages.no_permissions')}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('modules:table.columns.permission_name')}</TableCell>
                    <TableCell>{t('modules:table.columns.resource')}</TableCell>
                    <TableCell>{t('modules:table.columns.action')}</TableCell>
                    <TableCell>{t('modules:table.columns.description')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modulePermissions.map((permission) => (
                    <TableRow key={permission.permission_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {permission.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={permission.resource}
                          size="small"
                          color="grey"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={permission.action}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {permission.description || t('modules:messages.no_description')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={moduleDetailsDialog.closeDialog}>{t('modules:actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Department Modules Dialog */}
      <Dialog
        open={departmentModulesDialog.open}
        onClose={departmentModulesDialog.closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon />
            {t('modules:dialogs.departments_title', { name: departmentModulesDialog.data?.name })}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('modules:dialogs.departments_description', { name: departmentModulesDialog.data?.name })}
          </Typography>

          <List>
            {departmentModulesList.map((department) => (
              <ListItem
                key={department.department_id}
                divider
                sx={{
                  opacity: department.deactivated_at ? 0.6 : 1,
                }}
              >
                <ListItemIcon>
                  {department.hasModule ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <CancelIcon color="disabled" />
                  )}
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">
                        {department.name}
                      </Typography>
                      {department.deactivated_at && (
                        <Chip label={t('modules:status.deactivated')} size="small" color="warning" variant="outlined" />
                      )}
                    </Box>
                  }
                  secondary={
                    department.hasModule 
                      ? t('modules:messages.module_enabled')
                      : t('modules:messages.module_disabled')
                  }
                />
              </ListItem>
            ))}
          </List>

          {departmentModulesList.length === 0 && (
            <Alert severity="info">
              {t('modules:messages.no_departments')}
            </Alert>
          )}

          {departmentModulesList.filter(d => d.hasModule).length === 0 && departmentModulesList.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('modules:messages.module_not_enabled')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={departmentModulesDialog.closeDialog}>{t('modules:actions.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}